import { useQuery } from '@tanstack/react-query';
import { LogOut, Monitor, Moon, Sun, UserRound, Users } from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { logoUrl } from '@/lib/branding';
import { useTheme } from '@/lib/theme';
import type { PortalMe } from '@/lib/types';
import { cn, initials } from '@/lib/utils';

export function usePortalMe() {
  return useQuery({ queryKey: ['portal', 'me'], queryFn: () => api<PortalMe>('/portal/me'), staleTime: 60_000 });
}

/** Layout des Kundenportals: kompakte Kopfzeile, zentrierter Inhalt, mobil optimiert. */
export function PortalShell() {
  const { me, logout } = useAuth();
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const { data: portal } = usePortalMe();
  if (!me) return null;

  const link = ({ isActive }: { isActive: boolean }) =>
    cn('rounded-md px-3 py-1.5 text-sm font-medium transition-colors', isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground');

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <NavLink to="/portal" className="flex min-w-0 items-center gap-2.5">
            {me.tenant.hasLogo ? (
              <img src={logoUrl(me.tenant.brandingVersion)} alt="" className="h-8 max-w-[120px] object-contain" />
            ) : (
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">{me.tenant.name.charAt(0)}</span>
            )}
            <span className="hidden truncate text-sm font-semibold sm:block">{me.tenant.name}</span>
          </NavLink>
          <nav className="ml-2 flex gap-1">
            <NavLink to="/portal" end className={link}>
              Anfragen
            </NavLink>
            <NavLink to="/portal/repairs" className={link}>
              Reparaturen
            </NavLink>
            {portal?.canManageUsers && (
              <NavLink to="/portal/company" className={link}>
                Benutzer
              </NavLink>
            )}
          </nav>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex size-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary" aria-label="Benutzermenü">
                {initials(me.user.firstName, me.user.lastName)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <p className="font-medium">
                  {me.user.firstName} {me.user.lastName}
                </p>
                <p className="text-xs text-muted-foreground">{me.customer?.name}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/portal/profile')}>
                <UserRound /> Meine Daten
              </DropdownMenuItem>
              {portal?.canManageUsers && (
                <DropdownMenuItem onSelect={() => navigate('/portal/company')}>
                  <Users /> Benutzer meiner Firma
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setTheme('light')}>
                <Sun /> Hell
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('dark')}>
                <Moon /> Dunkel
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setTheme('system')}>
                <Monitor /> System
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={async () => {
                  await logout();
                  navigate('/login');
                }}
              >
                <LogOut /> Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
